import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Calendar, Target, Banknote, Activity, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fetchCampaign, fetchAllCreators } from "@/lib/api";
import { formatINR } from "@/lib/format";

const statusColor = {
  Approved: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  Submitted: "bg-blue-500/10 text-blue-300 border-blue-500/30",
  Pending: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  Rejected: "bg-red-500/10 text-red-300 border-red-500/30",
};

const CampaignDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [creators, setCreators] = useState({});
  const [notes, setNotes] = useState("");

  useEffect(() => {
    Promise.all([fetchCampaign(id), fetchAllCreators()])
      .then(([c, crs]) => {
        setCampaign(c);
        const map = {};
        crs.forEach((cr) => (map[cr.id] = cr));
        setCreators(map);
      })
      .catch(() => setCampaign(false));
  }, [id]);

  if (campaign === null) {
    return (
      <div className="flex items-center justify-center py-20 text-zinc-500">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (campaign === false) {
    return (
      <div className="rounded-xl border border-white/8 bg-white/[0.02] p-12 text-center">
        <p className="text-zinc-400">Campaign not found.</p>
        <Button className="mt-4" onClick={() => nav(-1)}>Go back</Button>
      </div>
    );
  }

  return (
    <div className="space-y-7" data-testid="campaign-detail-page">
      <Link to="/app/campaigns" className="text-sm text-zinc-400 hover:text-zinc-100 inline-flex items-center gap-1.5">
        <ArrowLeft className="h-4 w-4" /> All campaigns
      </Link>

      <div className="rounded-2xl border border-white/8 bg-gradient-to-br from-blue-500/[0.05] to-transparent p-6 lg:p-8">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs text-zinc-500 uppercase tracking-wider">{campaign.brand}</div>
            <h1 className="mt-1 text-3xl font-semibold tracking-tighter">{campaign.name}</h1>
            <p className="mt-2 text-zinc-300 max-w-2xl">{campaign.objective}</p>
          </div>
          <span className="px-3 py-1 rounded-md bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 text-xs uppercase tracking-wider">
            {campaign.stage}
          </span>
        </div>

        <div className="mt-7 grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { i: Target, k: "Platform", v: campaign.platform },
            { i: Calendar, k: "Dates", v: `${campaign.startDate} → ${campaign.endDate}` },
            { i: Banknote, k: "Budget", v: formatINR(campaign.budget) },
            { i: Activity, k: "Creators", v: String(campaign.creatorCount) },
          ].map(({ i: Icon, k, v }) => (
            <div key={k} className="rounded-xl border border-white/8 bg-white/[0.02] p-4">
              <div className="flex items-center gap-2 text-zinc-500 text-[11px] uppercase tracking-wider">
                <Icon className="h-3.5 w-3.5" /> {k}
              </div>
              <div className="mt-1.5 text-sm font-medium text-zinc-100">{v}</div>
            </div>
          ))}
        </div>
      </div>

      <section className="rounded-xl border border-white/8 bg-white/[0.02] p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold tracking-tight">Deliverables</h2>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-500 text-white" onClick={() => toast.success("Deliverable added")}>+ Add</Button>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="deliverables-table">
            <thead className="text-[11px] uppercase tracking-wider text-zinc-500">
              <tr className="text-left">
                <th className="py-2 font-medium">Creator</th>
                <th className="py-2 font-medium">Type</th>
                <th className="py-2 font-medium">Due</th>
                <th className="py-2 font-medium">Status</th>
                <th className="py-2 font-medium">Link</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {(campaign.deliverables || []).length === 0 ? (
                <tr><td colSpan="5" className="py-6 text-center text-zinc-500">No deliverables yet.</td></tr>
              ) : (
                campaign.deliverables.map((d, i) => {
                  const cr = creators[d.creator];
                  return (
                    <tr key={i}>
                      <td className="py-3">
                        {cr ? (
                          <Link to={`/app/creators/${cr.id}`} className="flex items-center gap-2 hover:text-blue-300">
                            <img src={cr.avatar} alt="" className="h-6 w-6 rounded-full object-cover" />
                            <span className="text-zinc-100">{cr.name}</span>
                          </Link>
                        ) : "—"}
                      </td>
                      <td className="py-3 text-zinc-300">{d.type}</td>
                      <td className="py-3 text-zinc-400">{d.due}</td>
                      <td className="py-3">
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${statusColor[d.status] || "bg-zinc-500/10 text-zinc-300 border-zinc-700"}`}>{d.status}</span>
                      </td>
                      <td className="py-3 text-blue-300 text-xs">{d.link ? <a href={d.link} target="_blank" rel="noreferrer" className="hover:underline">View</a> : "—"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-xl border border-white/8 bg-white/[0.02] p-5">
        <h2 className="text-base font-semibold tracking-tight">Milestone Timeline</h2>
        <div className="mt-5 relative pl-6 space-y-5 border-l border-white/10">
          {[
            { d: campaign.startDate, t: "Kickoff & creator onboarding" },
            { d: "Mid-campaign", t: "First batch deliverables in review" },
            { d: campaign.endDate, t: "Reporting & invoicing" },
          ].map((m, i) => (
            <div key={i} className="relative">
              <span className="absolute -left-[27px] top-1.5 h-2.5 w-2.5 rounded-full bg-blue-500 ring-4 ring-blue-500/20" />
              <div className="text-xs text-zinc-500">{m.d}</div>
              <div className="text-sm text-zinc-100">{m.t}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-xl border border-white/8 bg-white/[0.02] p-5">
        <h2 className="text-base font-semibold tracking-tight">Internal Notes</h2>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Add notes for the team…"
          className="mt-3 bg-white/[0.03] border-white/10 text-zinc-100"
          data-testid="campaign-notes-textarea"
        />
        <Button className="mt-3 bg-blue-600 hover:bg-blue-500 text-white" onClick={() => toast.success("Notes saved")}>Save</Button>
      </section>
    </div>
  );
};

export default CampaignDetail;
